import React, { FC, useEffect } from 'react';
import { useParams } from 'react-router';
import fp from 'lodash/fp';
import { formatDate } from '@factorin/util/lib';

import { useAction } from 'Modules/core/redux';
import { useDesktop, Stack, Typography, Box, useMobile } from 'Modules/ui3';
import { useStorageItem } from 'Modules/business/data';
import { Helmet } from 'Modules/core/helmet';

import { NewsDetail } from '../../components/NewsDetail';
import { PageContainer } from '../../components/PageContainer';
import { NewsItem, newsActions } from '../../state/news';
import { NEWS_DOMAIN_PATH } from '../../api';
import { NewsPreview } from '../../components/NewsPreview';
import { TelegramButton } from '../../components/Social';
import { News } from '../../components/News';
import { NewsSlideContainer } from '../../components/NewsSlide';


interface NewsItemHelmetProps {
    data: Pick<NewsItem, 'Content' | 'Date' | 'Tags'>;
}


const NewsItemHelmet: FC<NewsItemHelmetProps> = React.memo(({ data }) => (
    <Helmet>
        <meta property='og:url' content={window.location.href} />
        <meta
            property='og:title'
            content={fp.pipe(
                fp.get('Content.blocks'),
                fp.find({ type: 'header' }),
                fp.get('data.text'),
            )(data)}
        />
        <meta
            property='og:description'
            content={fp.pipe(
                fp.get('Content.blocks'),
                fp.find({ type: 'paragraph' }),
                fp.get('data.text'),
            )(data)}
        />
        <meta property='og:type' content='article' />
        <meta property='og:article:tag' content={data.Tags?.join(', ')} />
        <meta
            property='og:article:published_time'
            content={formatDate(data.Date)}
        />
        <meta property='og:site_name' content='ЦФА ХАБ' />
    </Helmet>
));


export const NewsDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const isDesktop = useDesktop();
    const isMobile = useMobile();
    const getNewsItem = useAction(
        () => newsActions.getItem({ id, screenType: isDesktop ? 'desktop' : 'mobile' }),
        [id, isDesktop],
    );

    useEffect(() => {
        getNewsItem();
    }, [getNewsItem]);

    const news = useStorageItem<NewsItem>(NEWS_DOMAIN_PATH, id);

    return (
        <PageContainer withMainButton>
            {news && <NewsItemHelmet data={news} />}
            <Stack direction={isDesktop ? 'row' : 'column'} alignItems={isDesktop ? 'flex-start' : 'normal'} spacing={isDesktop ? 2 : 6}>
                <Box flex={2}>
                    <NewsDetail RenderContent={news?.RenderContent} Tags={news?.Tags} />
                </Box>
                <Stack spacing={8} alignItems='flex-start' flex={1}>
                    {isDesktop && news?.Neighbours?.Next && (
                        <NewsSlideContainer>
                            <NewsPreview {...news?.Neighbours?.Next} />
                        </NewsSlideContainer>
                    )}
                    <Stack spacing={3} px={isDesktop ? 8 : 0} alignItems='flex-start'>
                        <Typography fontSize={isDesktop ? '20px' : '16px'} fontWeight='500' lineHeight={isDesktop ? '1.6' : '1.5'}>
                            Всё о рынке ЦФА, подписывайтесь!
                        </Typography>
                        <TelegramButton />
                    </Stack>
                </Stack>
            </Stack>
            <News rowCount={isMobile ? 2 : 1} />
        </PageContainer>
    );
};
